import { useEffect, useMemo, useState } from 'react';
import type { AuditEntry } from '../lib/types';
import { NotAuthenticatedError, api } from '../lib/api';

const ACTION_COLORS: Record<string, { bg: string; fg: string; bd: string }> = {
  create: { bg: 'var(--green-bg)', fg: 'var(--green-fg)', bd: 'var(--green-bd)' },
  update: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)', bd: 'var(--blue-bd)' },
  assign: { bg: 'var(--purple-bg)', fg: 'var(--purple-fg)', bd: 'var(--purple-bd)' },
  cancel: { bg: 'var(--red-bg)', fg: 'var(--red-fg)', bd: 'var(--red-bd)' },
};

/** UTC ISO from the API → the viewer's local time. */
function localStamp(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `dates: 2026-08-07 → 2026-08-21` for each changed field. */
function describeDiff(entry: AuditEntry): string[] {
  const after = entry.after ?? {};
  const before = entry.before ?? {};
  return Object.keys(after).map((key) => {
    const from = before[key];
    const to = after[key];
    const show = (v: unknown) =>
      v === null || v === undefined || v === '' ? '—' : String(v);
    return entry.before === null
      ? `${key}: ${show(to)}`
      : `${key}: ${show(from)} → ${show(to)}`;
  });
}

/**
 * The change trail. Admin only — the API refuses everyone else, and the
 * tab isn't rendered for them either.
 *
 * Rows are shown newest first with the human summary on the line and
 * the field-level diff underneath, so the common question ("who moved
 * this booking?") is answered without expanding anything.
 */
export function AuditLog({ onSignedOut }: { onSignedOut: () => void }) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [entityFilter, setEntityFilter] = useState<'all' | 'equipment' | 'reservation'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .listAudit(500)
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) onSignedOut();
        else setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [onSignedOut]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (entries ?? []).filter((e) => {
      if (entityFilter !== 'all' && e.entityType !== entityFilter) return false;
      if (needle === '') return true;
      return (
        e.summary.toLowerCase().includes(needle) ||
        e.actorName.toLowerCase().includes(needle) ||
        e.actorUpn.toLowerCase().includes(needle)
      );
    });
  }, [entries, entityFilter, search]);

  if (loadError !== null) {
    return (
      <div className="error-banner">Failed to load the audit log: {loadError}</div>
    );
  }
  if (entries === null) {
    return <div className="empty">Loading audit log…</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search summary or person…"
          style={{ flex: 1, minWidth: 200 }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {(['all', 'reservation', 'equipment'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setEntityFilter(k)}
              className="tab-btn"
              style={{
                background:
                  entityFilter === k ? 'var(--blue-bg)' : 'var(--bg-secondary)',
                color:
                  entityFilter === k ? 'var(--blue-fg)' : 'var(--text-secondary)',
                border: `0.5px solid ${
                  entityFilter === k ? 'var(--blue-bd)' : 'var(--border-md)'
                }`,
                borderRadius: 6,
                fontSize: 12,
                padding: '3px 10px',
                textTransform: 'capitalize',
              }}
            >
              {k === 'all' ? 'All' : `${k}s`}
            </button>
          ))}
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="empty">
          Nothing recorded yet. The log starts from the first change made
          after migration 005 was applied — it isn't backfilled, because
          there was no record to backfill from.
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty">No entries match that filter.</div>
      ) : (
        <div className="table">
          <div
            className="table-head"
            style={{ gridTemplateColumns: '1.4fr 0.8fr 3fr 1.4fr' }}
          >
            <span>When</span>
            <span>Action</span>
            <span>What changed</span>
            <span>Who</span>
          </div>
          {filtered.map((e) => {
            const colors = ACTION_COLORS[e.action] ?? {
              bg: 'var(--bg-secondary)',
              fg: 'var(--text-secondary)',
              bd: 'var(--border-md)',
            };
            const diff = describeDiff(e);
            return (
              <div
                key={e.id}
                className="table-row"
                style={{
                  gridTemplateColumns: '1.4fr 0.8fr 3fr 1.4fr',
                  cursor: 'default',
                  alignItems: 'start',
                }}
              >
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {localStamp(e.createdAt)}
                </span>
                <span>
                  <span
                    className="badge"
                    style={{
                      background: colors.bg,
                      color: colors.fg,
                      border: `0.5px solid ${colors.bd}`,
                    }}
                  >
                    {e.action}
                  </span>
                </span>
                <span style={{ fontSize: 12 }}>
                  {e.summary}
                  {diff.length > 0 && (
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-tertiary)',
                        marginTop: 2,
                      }}
                    >
                      {diff.join(' · ')}
                    </div>
                  )}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {e.actorName || e.actorUpn || '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
